import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import AuthCallback from './components/AuthCallback';
import Home from './pages/Home';
import LoginPage from './pages/Login';
import CampaignBoard from './pages/CampaignBoard';
import CampaignDetail from './pages/CampaignDetail';
import ForBrands from './pages/ForBrands';
import CaseStudies from './pages/CaseStudies';
import IndustryDeck from './pages/IndustryDeck';
import Onboarding from './pages/Onboarding';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import AdminPanel from './pages/AdminPanel';
import BrandPortal from './pages/BrandPortal';
import PaymentSuccess from './pages/PaymentSuccess';
import CampaignLanding from './pages/CampaignLanding';
import OfferPage from './pages/OfferPage';
import ProposalSignPage from './pages/ProposalSignPage';
import BrandOnboarding from './pages/BrandOnboarding';
import AdminRecovery from './pages/AdminRecovery';
import { TermsOfService, PrivacyPolicy } from './pages/Legal';
import { trackPageview } from './utils/attribution';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function HomeOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#090E33] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" /></div>;
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'executive') return <Navigate to="/dashboard" replace />;
  return <Home />;
}

function AppRouter() {
  const location = useLocation();

  // SPA page-view tracking — fires GA4 + Meta Pixel on every route change.
  // Without this, only the initial page load is tracked, undercounting traffic dramatically.
  useEffect(() => {
    const path = location.pathname + location.search;
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    // Only fire Meta Pixel if not on a logged-in page (we suppressed pixel load there)
    if (typeof window !== 'undefined' && typeof window.fbq === 'function' && !window._fbDisabled) {
      window.fbq('track', 'PageView');
    }
    // Server-side attribution / UTM capture — gives us a Reddit/LinkedIn/etc.
    // click count independent of client-side analytics.
    trackPageview(API);
  }, [location.pathname, location.search]);

  // CRITICAL: Detect session_id during render for Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hide global navbar on pages that have their own navigation
  const hideNavbar = location.pathname === '/portal' || location.pathname === '/login' || location.pathname === '/proposal' || location.pathname === '/admin/recovery' || location.pathname.startsWith('/portal/onboarding') || location.pathname.startsWith('/c/') || location.pathname.startsWith('/offer/');

  return (
    <>
      {!hideNavbar && <Navbar />}
      <Routes>
        <Route path="/" element={<HomeOrDashboard />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/campaigns" element={<CampaignBoard />} />
        <Route path="/campaigns/:id" element={<CampaignDetail />} />
        <Route path="/for-brands" element={<ForBrands />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/deck/:industry" element={<IndustryDeck />} />
        <Route path="/portal" element={<BrandPortal />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/c/:id" element={<CampaignLanding />} />
        <Route path="/offer/:id" element={<OfferPage />} />
        <Route path="/proposal" element={<ProposalSignPage />} />
        <Route path="/portal/onboarding" element={<BrandOnboarding />} />
        <Route path="/admin/recovery" element={<AdminRecovery />} />
        <Route path="/payments/success" element={<PaymentSuccess />} />
        <Route path="/onboarding" element={
          <ProtectedRoute><Onboarding /></ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute><ExecutiveDashboard /></ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminRoute><AdminPanel /></AdminRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthGate>
          <AppRouter />
        </AuthGate>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AuthGate({ children }) {
  const { loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-10 w-10 animate-pulse" />
          <div className="w-6 h-6 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  return children;
}

export default App;
